import { useWalletFormReset } from "../base-app/hooks/useWalletFormReset";
import { useDepositMergeWarmup } from "../joinsplit-v2/useDepositMergeWarmup";
import { useBaseStatusDismissal } from "../base-app/hooks/useBaseStatusDismissal";
import { solanaPaymentLink } from "./helpers/paymentLink";
import { isSolanaHandleInput } from "./hooks/useSolanaHandleRecipient";
import { useStealthHandleRegistration } from "../base-app/hooks/useStealthHandleRegistration";
import { BaseHandlePanel } from "../base-app/components/BaseHandlePanel";
import { RecoveryPhraseBackup } from "../base-app/components/RecoveryPhraseBackup";
import { solanaHandleSigner } from "../utils/solana-handle-ownership";
import { useSolanaHandleRecipient } from "./hooks/useSolanaHandleRecipient";
import { useSolanaWithdrawalQuote } from "./hooks/useSolanaWithdrawalQuote";
import { BaseTransactionReview } from "../base-app/components/BaseTransactionReview";
import type { WalletBatchRow } from "../base-app/components/WalletBatchRecipients";
import { useSolanaDepositQuote } from "./hooks/useSolanaDepositQuote";
import {
  SolanaDepositFeeSummary,
  SolanaSendFeeSummary,
} from "./components/SolanaFeeSummary";
import { solanaAmount, solanaSendAmounts } from "./helpers/amounts";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { BaseStatusMessage, BaseTabType } from "../base-app/types";
import { BaseWalletShell } from "../base-app/components/BaseWalletShell";
import { BasePrivateBalancesCard } from "../base-app/components/BasePrivateBalancesCard";
import { BaseReceiveTab } from "../base-app/components/BaseReceiveTab";
import { BaseOperationProgress } from "../base-app/components/BaseOperationProgress";
import { useBlockNetworkSwitch } from "../networks/WalletNetworkContext";
import { useMarketPrices } from "../app-shell/useMarketPrices";
import { balanceUsdValue } from "../utils/market-prices";
import type { TokenInfo } from "../config";
import { BaseHistoryCard } from "../base-app/components/BaseHistoryCard";
import {
  formatSolanaTokenAmount,
  solanaPriceAssets,
  solanaTokenLabel,
  solanaExplorerTxUrl,
  type SolanaNetworkConfig,
  type SolanaTokenInfo,
} from "../config/solana";
import {
  createSolanaBrowserRpc,
  isSolanaFlatNote,
  solanaNoteMint,
} from "../joinsplit-v2/solana";
import { styles } from "../styles";
import { formatMpkHex } from "../utils/format";
import {
  formatInboxPublicKeyHex,
  parsePrivateAddress,
} from "../utils/inbox-crypto";
import { SolanaLogPanel } from "./components/SolanaLogPanel";
import { SolanaSendTab } from "./components/SolanaSendTab";
import { SolanaShieldTab } from "./components/SolanaShieldTab";
import { SolanaTestTokenNotice } from "./components/SolanaTestTokenNotice";
import { SolanaSwapTab } from "./components/SolanaSwapTab";
import SolanaWalletModal from "./components/SolanaWalletModal";
import {
  classifySolanaRecipient,
  useSolanaActions,
} from "./hooks/useSolanaActions";
import { useSolanaNotes } from "./hooks/useSolanaNotes";
import { pendingSettlementDetails } from "./pendingSettlementDetails";
import { useSolanaProxyAuth } from "./hooks/useSolanaProxyAuth";
import { useSolanaWallet, useSolanaWallets } from "./hooks/useSolanaWallet";
import type { SolanaLogEntry, SolanaLogType } from "./types";

const TABS: { id: BaseTabType; label: string }[] = [
  { id: "shield", label: "Add" },
  { id: "receive", label: "Receive" },
  { id: "send", label: "Send" },
  { id: "swap", label: "Swap" },
  { id: "crosschain", label: "Bridge" },
];

export default function SolanaApp({
  network,
}: {
  network: SolanaNetworkConfig;
}) {
  const rpc = useMemo(
    () => createSolanaBrowserRpc(network.rpcUrl),
    [network.rpcUrl],
  );
  const [linkRequest, setLinkRequest] = useState(() =>
    solanaPaymentLink(window.location.search, network),
  );
  const paymentRequest = linkRequest?.mode === "deposit" ? linkRequest : null;
  const [activeTab, setActiveTab] = useState<BaseTabType>(
    linkRequest?.mode === "send" ? "send" : "shield",
  );
  const [status, setStatus] = useState<BaseStatusMessage | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [logs, setLogs] = useState<SolanaLogEntry[]>([]);
  const logIdRef = useRef(0);
  const addLog = useCallback(
    (message: string, type: SolanaLogType = "info", signature?: string) => {
      logIdRef.current += 1;
      const entry: SolanaLogEntry = {
        id: logIdRef.current,
        message,
        type,
        timestamp: new Date(),
        signature,
      };
      setLogs((current) => [...current.slice(-199), entry]);
    },
    [],
  );

  const wallets = useSolanaWallets();
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const { wallet, connectWallet, disconnectWallet, derivationSignatureRef } =
    useSolanaWallet({
      network,
      addLog,
    });
  const auth = useSolanaProxyAuth({ network, wallet, derivationSignatureRef });
  const handleSigner = useMemo(
    () => (wallet.connection ? solanaHandleSigner(wallet.connection) : null),
    [wallet.connection],
  );
  const handleSignatureRef = useRef("");
  handleSignatureRef.current = derivationSignatureRef.current
    ? btoa(String.fromCharCode(...derivationSignatureRef.current))
    : "";
  const handles = useStealthHandleRegistration({
    signer: handleSigner,
    network: network.key,
    spendingKeys: wallet.spendingKeys,
    proxyAuthReady: auth.proxyAuthReady,
    derivationSignatureB64Ref: handleSignatureRef,
    ensureProxyAuthToken: auth.ensureProxyAuthToken,
    addLog,
  });
  const signInConnectionRef = useRef<typeof wallet.connection>(null);
  // Connecting updates React state. Start authentication from the render that
  // owns the new connection, rather than the disconnected event-handler closure.
  useEffect(() => {
    if (!wallet.connected || !wallet.connection) {
      signInConnectionRef.current = null;
      return;
    }
    if (signInConnectionRef.current === wallet.connection) return;
    signInConnectionRef.current = wallet.connection;
    setStatus(null);
    void auth.ensureProxyAuthToken({ interactive: true }).catch((error) => {
      const message = error instanceof Error ? error.message : String(error);
      addLog(message, "error");
      setStatus({ type: "error", message });
    });
  }, [addLog, auth.ensureProxyAuthToken, wallet.connected, wallet.connection]);
  const notes = useSolanaNotes({
    network,
    rpc,
    wallet,
    ensureProxyAuthToken: auth.ensureProxyAuthToken,
    authHeadersForPath: auth.authHeadersForPath,
    clearProxyAuthToken: auth.clearProxyAuthToken,
    addLog,
  });
  const actions = useSolanaActions({
    network,
    rpc,
    wallet,
    notes,
    ensureProxyAuthToken: auth.ensureProxyAuthToken,
    addLog,
    setStatus,
  });
  useBlockNetworkSwitch(
    actions.busy || handles.busy || auth.proxyAuthPending || connecting,
  );

  const [shieldToken, setShieldToken] = useState<SolanaTokenInfo>(
    paymentRequest?.token ?? network.tokens[0],
  );
  const [shieldAmount, setShieldAmount] = useState(
    paymentRequest?.amount ?? "",
  );
  const [shieldMemo, setShieldMemo] = useState("");

  const [sendToken, setSendToken] = useState<SolanaTokenInfo>(
    (linkRequest?.mode === "send" ? linkRequest.token : null) ??
      network.tokens[0],
  );
  const [recipient, setRecipient] = useState(
    linkRequest?.mode === "send" ? linkRequest.recipient : "",
  );
  const [sendAmount, setSendAmount] = useState(
    linkRequest?.mode === "send" ? linkRequest.amount : "",
  );
  const [sendMemo, setSendMemo] = useState("");
  const [batchMode, setBatchMode] = useState(false);
  const [batchRows, setBatchRows] = useState<WalletBatchRow[]>([
    { recipient: "", amount: "" },
  ]);
  const [review, setReview] = useState<"shield" | "send" | null>(null);
  const [acceptedSend, setAcceptedSend] = useState<{
    account: string;
    network: string;
    quote: ReturnType<typeof useSolanaWithdrawalQuote>["quote"];
    balance: bigint;
  } | null>(null);
  const [operationOpen, setOperationOpen] = useState(false);
  useDepositMergeWarmup(
    wallet.connected &&
      auth.proxyAuthReady &&
      operationOpen &&
      activeTab === "shield" &&
      !paymentRequest &&
      network.depositMergeEnabled
      ? `${network.key}:${wallet.address}:${shieldToken.mint}`
      : null,
    actions.busy,
    notes.balances.some(
      (row) => row.mint === shieldToken.mint && row.spendable > 0n,
    ),
  );
  const depositQuote = useSolanaDepositQuote({
    rpc,
    onchainRecoveryEnabled: network.onchainRecoveryEnabled,
    networkKey: network.key,
    account: wallet.address,
    token: shieldToken,
    depositMergeEnabled:
      network.depositMergeEnabled === true &&
      (!paymentRequest ||
        (!paymentRequest.error &&
          BigInt(parsePrivateAddress(paymentRequest.recipient)!.mpkHex) ===
            wallet.spendingKeys?.mpk)),
    enabled:
      wallet.connected &&
      auth.proxyAuthReady &&
      operationOpen &&
      activeTab === "shield" &&
      !actions.busy,
    setAmount: setShieldAmount,
  });
  const walletBalance = depositQuote.quote
    ? formatSolanaTokenAmount(depositQuote.quote.balance, shieldToken.decimals)
    : null;
  useEffect(() => {
    setReview(null);
    setAcceptedSend(null);
  }, [wallet.address, network.key]);
  const [paymentLinkToken, setPaymentLinkToken] =
    useState<SolanaTokenInfo | null>(null);
  const [paymentLinkAmount, setPaymentLinkAmount] = useState("");

  useWalletFormReset(operationOpen, activeTab, () => {
    depositQuote.changeAmount("");
    setSendAmount("");
    setBatchRows((rows) => rows.map((row) => ({ ...row, amount: "" })));
    setReview(null);
    if (linkRequest) {
      setLinkRequest(null);
      window.history.replaceState(null, "", network.route);
    }
  });

  useBaseStatusDismissal(
    status,
    setStatus,
    actions.busy ||
      handles.busy ||
      connecting ||
      notes.syncing ||
      auth.proxyAuthPending,
  );

  // Sync notes and history once the session is ready.
  useEffect(() => {
    if (
      !wallet.connected ||
      (!auth.proxyAuthReady && !network.onchainRecoveryEnabled)
    )
      return;
    void notes.syncNotes();
    if (auth.proxyAuthReady) void notes.syncHistory();
  }, [
    wallet.connected,
    auth.proxyAuthReady,
    wallet.address,
    network.onchainRecoveryEnabled,
  ]);

  const handleWalletButton = useCallback(() => {
    if (wallet.connected) {
      auth.clearProxyAuthToken();
      void disconnectWallet();
      setStatus(null);
    } else {
      setWalletModalOpen(true);
    }
  }, [auth, disconnectWallet, wallet.connected]);

  const handleSelectWallet = useCallback(
    async (
      candidate: Parameters<typeof connectWallet>[0],
      options?: Parameters<typeof connectWallet>[1],
    ) => {
      setWalletModalOpen(false);
      setConnecting(true);
      try {
        await connectWallet(candidate, options);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        addLog(message, "error");
        setStatus({ type: "error", message });
      } finally {
        setConnecting(false);
      }
    },
    [addLog, connectWallet],
  );

  const privateAddress = wallet.spendingKeys
    ? `${formatMpkHex(wallet.spendingKeys.mpk)}:${formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey)}`
    : null;

  const copyPrivateAddress = useCallback(() => {
    if (!privateAddress) return;
    void navigator.clipboard.writeText(privateAddress);
    addLog("Private address copied", "success");
    setStatus({
      type: "success",
      message: "Private address copied",
      autoDismissMs: 4000,
    });
  }, [addLog, privateAddress]);

  const copyPaymentLink = useCallback(() => {
    if (!privateAddress) return;
    const url = new URL(window.location.href);
    url.pathname = `${network.route}/send`;
    url.search = "";
    url.searchParams.set("vault", privateAddress);
    url.searchParams.set("network", network.key);
    if (paymentLinkToken)
      url.searchParams.set("token", paymentLinkToken.mint ?? "sol");
    if (paymentLinkAmount.trim())
      url.searchParams.set("amount", paymentLinkAmount.trim());
    void navigator.clipboard.writeText(url.toString());
    addLog("Payment link copied", "success");
    setStatus({
      type: "success",
      message: "Payment link copied",
      autoDismissMs: 4000,
    });
  }, [
    addLog,
    network.key,
    network.route,
    paymentLinkAmount,
    paymentLinkToken,
    privateAddress,
  ]);

  const handleRecipient = useSolanaHandleRecipient(recipient, network.key);
  const recipientKind = classifySolanaRecipient(
    handleRecipient.address ?? recipient,
  );
  useEffect(() => {
    setReview(null);
  }, [recipient, handleRecipient.address, network.key]);
  const sendBalance = notes.balances.find((row) => row.mint === sendToken.mint);
  const sendInputs = batchMode
    ? batchRows.map((row) => row.amount)
    : [sendAmount];
  const parsedSendInputs = sendInputs.map((value) =>
    solanaAmount(value, sendToken),
  );
  const sendQuoteAmount = parsedSendInputs.every((value) => value !== null)
    ? parsedSendInputs.reduce<bigint>(
        (total, value) => total + (value ?? 0n),
        0n,
      )
    : null;
  const quoteRecipients = batchMode
    ? batchRows.map((row) => row.recipient.trim())
    : [recipient.trim()];
  const liveWithdrawalQuote = useSolanaWithdrawalQuote({
    context: {
      network,
      rpc,
      relay: notes.relay,
      notes: notes.notes,
      mint: sendToken.mint,
      recipients: quoteRecipients,
    },
    account: wallet.address,
    amount: sendQuoteAmount,
    batch: batchMode,
    enabled:
      wallet.connected &&
      auth.proxyAuthReady &&
      operationOpen &&
      activeTab === "send" &&
      !actions.busy &&
      !acceptedSend &&
      (batchMode
        ? batchRows.length >= 2 &&
          quoteRecipients.every(
            (value) => classifySolanaRecipient(value) === "wallet",
          )
        : recipientKind === "wallet"),
  });
  const accepted =
    acceptedSend?.account === wallet.address &&
    acceptedSend.network === network.key
      ? acceptedSend
      : null;
  // The submitted operation owns this quote. Live note reservations, expiry,
  // and background refreshes must not replace its fee breakdown on screen.
  const withdrawalQuote = accepted
    ? {
        ...liveWithdrawalQuote,
        quote: accepted.quote,
        error: null,
        loading: false,
        batchMaxLoading: false,
        batchMaxError: null,
      }
    : liveWithdrawalQuote;
  const displayedSendBalance =
    accepted?.balance ?? sendBalance?.spendable ?? 0n;
  const sendPreview = solanaSendAmounts(
    network,
    sendToken,
    batchMode ? batchRows.map((row) => row.amount) : [sendAmount],
    displayedSendBalance,
    batchMode ? "batch" : recipientKind,
    withdrawalQuote.quote ? BigInt(withdrawalQuote.quote.quote.fee) : null,
  );
  const shieldValue = solanaAmount(shieldAmount, shieldToken);
  const confirmReview = () => {
    if (!review || actions.busy || !wallet.connected || !auth.proxyAuthReady)
      return;
    if (
      review === "shield"
        ? !!paymentRequest?.error ||
          !shieldValue ||
          !depositQuote.quote ||
          depositQuote.loading ||
          shieldValue > depositQuote.quote.max
        : !!sendPreview.error
    )
      return;
    const operation = review;
    setReview(null);
    setStatus(null);
    if (operation === "shield")
      void actions.handleShield(
        shieldAmount,
        shieldToken,
        shieldMemo || undefined,
        paymentRequest?.recipient,
      );
    else {
      const snapshot = {
        account: wallet.address,
        network: network.key,
        quote: liveWithdrawalQuote.quote,
        balance: sendBalance?.spendable ?? 0n,
      };
      setAcceptedSend(snapshot);
      const pending = batchMode
        ? actions.handleBatchWithdraw(
            batchRows.map((row) => ({
              owner: row.recipient,
              amount: row.amount,
            })),
            sendToken,
            undefined,
            snapshot.quote ?? undefined,
          )
        : actions.handleSend({
            recipient,
            resolvedPrivateAddress: handleRecipient.address ?? undefined,
            amountInput: sendAmount,
            feeQuote: snapshot.quote ?? undefined,
            token: sendToken,
            memo: isSolanaHandleInput(recipient)
              ? sendMemo || undefined
              : undefined,
          });
      void Promise.resolve(pending).finally(() =>
        setAcceptedSend((current) => (current === snapshot ? null : current)),
      );
    }
  };
  const retiredTrees = useMemo(() => {
    const current = Math.max(
      -1,
      ...notes.notes
        .filter((stored) => !stored.spent && stored.note.leafIndex >= 0)
        .map((stored) => stored.note.treeIndex ?? -1),
    );
    const groups = new Map<
      string,
      {
        treeIndex: number;
        token: SolanaTokenInfo;
        total: bigint;
        count: number;
      }
    >();
    for (const stored of notes.notes) {
      const note = stored.note;
      if (
        stored.spent ||
        note.leafIndex < 0 ||
        note.treeIndex === undefined ||
        note.treeIndex >= current
      )
        continue;
      if (!isSolanaFlatNote(note, network)) continue;
      let mint: string | null;
      try {
        mint = solanaNoteMint(note);
      } catch {
        continue;
      }
      const token = network.tokens.find((candidate) => candidate.mint === mint);
      if (!token) continue;
      const key = `${note.treeIndex}:${token.symbol}`;
      const group = groups.get(key) ?? {
        treeIndex: note.treeIndex,
        token,
        total: 0n,
        count: 0,
      };
      group.total += note.value;
      group.count += 1;
      groups.set(key, group);
    }
    return [...groups.values()].map((group) => ({
      treeIndex: group.treeIndex,
      token: group.token,
      total: formatSolanaTokenAmount(group.total, group.token.decimals),
      count: group.count,
    }));
  }, [network, notes.notes]);

  const explorerTxUrl = useCallback(
    (signature: string) => solanaExplorerTxUrl(network, signature),
    [network],
  );

  const priceAssets = useMemo(
    () => solanaPriceAssets(network.tokens),
    [network.tokens],
  );
  const {
    prices,
    changes: priceChanges,
    refresh: refreshPrices,
  } = useMarketPrices(priceAssets);
  const balanceRows = useMemo(
    () =>
      notes.balances
        .filter((row) => row.spendable > 0n || row.pending > 0n)
        .map((row) => {
          const token = network.tokens.find((t) => t.mint === row.mint);
          return {
            id: row.mint ?? "sol",
            mint: row.mint ?? "sol",
            symbol: row.symbol,
            name: token ? solanaTokenLabel(token) : row.symbol,
            iconId: token?.iconId,
            amount: row.spendableDisplay,
            isNft: false,
            pendingAmount: row.pending > 0n ? row.pendingDisplay : undefined,
          };
        }),
    [notes.balances, network.tokens],
  );
  const valuation = useMemo(
    () => balanceUsdValue(balanceRows, prices),
    [balanceRows, prices],
  );
  const receiveTokens = useMemo<TokenInfo[]>(
    () =>
      network.tokens.map((token) => ({
        address: token.mint ?? "sol",
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
        isNative: token.isNative,
      })),
    [network.tokens],
  );
  const refresh = async () => {
    await Promise.all([
      notes.syncNotes(),
      ...(auth.proxyAuthReady ? [notes.syncHistory()] : []),
      refreshPrices(),
    ]);
  };
  const resumeRecoveredSettlement = (recordId: string, backupId?: string) => {
    setStatus({
      type: "info",
      message: "Checking and submitting the selected settlement…",
    });
    void notes
      .syncNotes(recordId, backupId)
      .then((result) => {
        if (!result) return;
        const failure = result.failed.find((item) => item.id === recordId);
        setStatus(
          failure
            ? {
                type: "error",
                message: `Settlement was refused: ${failure.error}`,
              }
            : {
                type: "info",
                message: result.stillPending.includes(recordId)
                  ? "Settlement is pending. Refresh recovery to check its chain status."
                  : "Settlement confirmed. Refresh recovery to check note delivery.",
              },
        );
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message: `Recovery retry was not submitted: ${error instanceof Error ? error.message : String(error)}`,
        });
      });
  };

  if (!network.enabled) {
    return (
      <div style={styles.app}>
        <div
          style={{ ...styles.card, maxWidth: "720px", margin: "48px auto 0" }}
        >
          <h2 style={{ marginTop: 0, color: "#e8eeff" }}>
            {network.label} is unavailable
          </h2>
          <p style={{ color: "#b0c4de" }}>
            {network.configurationError
              ? `This deployment's Solana configuration is invalid: ${network.configurationError}`
              : "This deployment has no Solana pool configured (REACT_APP_SOLANA_DEVNET_POOL_PROGRAM_ID)."}
          </p>
          <a href="/app" style={{ color: "#60a5fa" }}>
            Choose another network
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      {walletModalOpen && (
        <SolanaWalletModal
          wallets={wallets}
          chainLabel={network.label}
          onSelect={(candidate, options) =>
            void handleSelectWallet(candidate, options)
          }
          onClose={() => setWalletModalOpen(false)}
        />
      )}
      <BaseWalletShell
        connected={wallet.connected}
        walletAddress={wallet.address}
        claimedHandle={handles.claimedHandle}
        handlesAvailable={handles.handlesSupported}
        chainLabel={network.label}
        busy={actions.busy || handles.busy || connecting}
        status={status}
        activeTab={activeTab}
        tabs={TABS}
        onSelectTab={(tab) => {
          setActiveTab(tab);
          setReview(null);
          setStatus(null);
        }}
        onOperationVisibilityChange={setOperationOpen}
        initialOperation={!!linkRequest}
        disabledActions={{
          crosschain: "Bridge is not available on Solana Devnet",
        }}
        onWallet={handleWalletButton}
        onCopyHandle={() => void handles.copyHandle()}
        canCopyHandle={!!handles.claimedHandle}
        balanceUsd={valuation.total}
        priceCaption={
          valuation.total === null
            ? "Price unavailable"
            : "Testnet reference value" +
              (valuation.unpriced
                ? ` · Partial: ${valuation.unpriced} unpriced assets`
                : "")
        }
        balanceCaption={
          balanceRows.length
            ? `${balanceRows.length} private assets`
            : "No private assets yet"
        }
        balancesReady={
          balanceRows.length > 0 ||
          ((auth.proxyAuthReady || network.onchainRecoveryEnabled === true) &&
            !notes.syncing)
        }
        balanceUnavailable={
          wallet.connected &&
          !auth.proxyAuthReady &&
          !network.onchainRecoveryEnabled &&
          !balanceRows.length
        }
        refreshing={notes.syncing}
        canRefresh={
          wallet.connected &&
          (auth.proxyAuthReady || network.onchainRecoveryEnabled === true) &&
          !actions.busy
        }
        onRefresh={() => void refresh()}
        reviewing={!!review}
        onBackFromReview={() => setReview(null)}
        authNotice={
          wallet.connected && !auth.proxyAuthReady ? (
            <div className="wallet-notice wallet-notice-info">
              <p>
                {auth.proxyAuthError ||
                  (auth.proxyAuthPending
                    ? "Confirm the sign-in request in your wallet."
                    : network.onchainRecoveryEnabled
                      ? "Sign in to submit sponsored transactions. Wallet recovery is available."
                      : "Sign in to access your private wallet.")}
              </p>
              <button
                type="button"
                className="wallet-secondary"
                disabled={auth.proxyAuthPending}
                onClick={() =>
                  void auth
                    .ensureProxyAuthToken({ interactive: true })
                    .catch(() => undefined)
                }
              >
                {auth.proxyAuthPending ? "Waiting…" : "Sign in"}
              </button>
            </div>
          ) : null
        }
        assets={
          <BasePrivateBalancesCard
            connected={wallet.connected}
            balancesSyncing={notes.syncing}
            rows={balanceRows}
            tokenPrices={prices}
            tokenMarkets={priceAssets}
          />
        }
        portfolio={{
          rows: balanceRows,
          prices,
          changes: priceChanges,
          markets: priceAssets,
          syncing: notes.syncing,
        }}
        history={
          <BaseHistoryCard
            historyLoading={notes.historyLoading}
            historyError={notes.historyError}
            historyEntries={notes.history}
            assetLabel={(symbol) => {
              const token = network.tokens.find(
                (entry) => entry.symbol === symbol,
              );
              return token ? solanaTokenLabel(token) : symbol;
            }}
            onRefresh={notes.syncHistory}
            getExplorerTxUrl={explorerTxUrl}
          />
        }
        handle={
          handles.handlesSupported ? (
            <BaseHandlePanel
              {...handles}
              canReceivePrivate={
                wallet.connected &&
                auth.proxyAuthReady &&
                !!wallet.spendingKeys &&
                !handles.busy &&
                !actions.busy
              }
            />
          ) : null
        }
        recovery={
          <div>
            <RecoveryPhraseBackup
              masterRecovery={wallet.spendingKeys?.masterRecovery}
              spendingKey={wallet.spendingKeys?.spendingKey ?? null}
              recoveryEntropy={wallet.spendingKeys?.recoveryEntropy}
              scopeLabel={network.label}
              walletIdentity={`${network.key}:${network.programId}:${wallet.address}:${wallet.spendingKeys?.mpk ?? ""}`}
            />
            <h3>Recovery & activity</h3>
            <p className="wallet-review-context">
              Refresh to check your encrypted inbox and reconcile pending
              settlements on {network.label}. Keep your original wallet and
              privacy credentials.
            </p>
            {notes.pendingSubmissions.map((record) => (
              <div className="wallet-notice wallet-notice-info" key={record.id}>
                {notes.conflictedSubmissionIds.includes(record.id) && (
                  <p>
                    Multiple backed-up intents spend these same notes. Recovery
                    is checking which one finalized. Review and explicitly
                    choose one version below if you decide to submit.
                  </p>
                )}
                <p>
                  {record.submissionAttempted || record.signature
                    ? "Earlier submission may still be pending"
                    : "Prepared settlement"}{" "}
                  on {new Date(record.createdAt).toLocaleString()}.
                  Reconciliation has not confirmed it on chain. No retry starts
                  automatically.
                </p>
                {record.signature && (
                  <p>Known transaction signature: {record.signature}</p>
                )}
                {pendingSettlementDetails(record, network).map(
                  (detail, index) => (
                    <p key={index} className="wallet-review-context">
                      {detail}
                    </p>
                  ),
                )}
                <button
                  type="button"
                  className="wallet-secondary"
                  disabled={
                    notes.syncing ||
                    actions.busy ||
                    !auth.proxyAuthReady ||
                    notes.conflictedSubmissionIds.includes(record.id)
                  }
                  onClick={() => resumeRecoveredSettlement(record.id)}
                >
                  {record.submissionAttempted || record.signature
                    ? "Retry this exact settlement"
                    : "Submit this prepared settlement"}
                </button>
                {notes.conflictedSubmissionIds.includes(record.id) &&
                  (notes.conflictedSubmissionOptions[record.id] ?? []).map(
                    (option) => (
                      <div
                        className="wallet-review-context"
                        key={option.backupId}
                      >
                        <p>
                          Backed-up version prepared on{" "}
                          {new Date(option.record.createdAt).toLocaleString()}:
                        </p>
                        {pendingSettlementDetails(option.record, network).map(
                          (detail, index) => (
                            <p key={index}>{detail}</p>
                          ),
                        )}
                        <button
                          type="button"
                          className="wallet-secondary"
                          disabled={
                            notes.syncing ||
                            actions.busy ||
                            !auth.proxyAuthReady
                          }
                          onClick={() =>
                            resumeRecoveredSettlement(
                              record.id,
                              option.backupId,
                            )
                          }
                        >
                          Submit this exact backed-up intent
                        </button>
                      </div>
                    ),
                  )}
              </div>
            ))}
            {notes.conflictedSubmissionIds
              .filter(
                (id) =>
                  !notes.pendingSubmissions.some((record) => record.id === id),
              )
              .map((id) => (
                <div className="wallet-notice wallet-notice-info" key={id}>
                  <p>
                    Multiple encrypted versions of settlement {id.slice(0, 12)}…
                    remain unconfirmed. Review an exact version before
                    submitting.
                  </p>
                  {(notes.conflictedSubmissionOptions[id] ?? []).map(
                    (option) => (
                      <div
                        className="wallet-review-context"
                        key={option.backupId}
                      >
                        {pendingSettlementDetails(option.record, network).map(
                          (detail, index) => (
                            <p key={index}>{detail}</p>
                          ),
                        )}
                        <button
                          type="button"
                          className="wallet-secondary"
                          disabled={
                            notes.syncing ||
                            actions.busy ||
                            !auth.proxyAuthReady
                          }
                          onClick={() =>
                            resumeRecoveredSettlement(id, option.backupId)
                          }
                        >
                          Submit this exact backed-up intent
                        </button>
                      </div>
                    ),
                  )}
                </div>
              ))}
            <button
              type="button"
              className="wallet-secondary"
              disabled={
                notes.syncing ||
                actions.busy ||
                (!auth.proxyAuthReady && !network.onchainRecoveryEnabled)
              }
              onClick={() => void refresh()}
            >
              Refresh recovery
            </button>
            <SolanaLogPanel logs={logs} explorerTxUrl={explorerTxUrl} />
          </div>
        }
      >
        {review && (
          <BaseTransactionReview
            kind={
              review === "shield"
                ? "deposit"
                : batchMode
                  ? "batch"
                  : recipientKind === "wallet"
                    ? "withdrawal"
                    : "private"
            }
            amount={
              review === "shield"
                ? shieldAmount
                : formatSolanaTokenAmount(
                    sendPreview.amount,
                    sendToken.decimals,
                  )
            }
            symbol={review === "shield" ? shieldToken.symbol : sendToken.symbol}
            recipient={
              review === "shield"
                ? (paymentRequest?.recipient ?? "Your private balance")
                : batchMode
                  ? batchRows
                      .map(
                        (row) =>
                          row.recipient +
                          " · " +
                          row.amount +
                          " " +
                          sendToken.symbol,
                      )
                      .join("; ")
                  : recipient
            }
            network={network.label}
            fee="0"
            feeLabel="Total fee"
            feeSummary={
              review === "shield" ? (
                <SolanaDepositFeeSummary
                  token={shieldToken}
                  amount={shieldValue ?? 0n}
                  quote={depositQuote.quote}
                  error={depositQuote.error}
                />
              ) : (
                <SolanaSendFeeSummary
                  token={sendToken}
                  preview={sendPreview}
                  quote={withdrawalQuote.quote?.quote ?? null}
                  error={withdrawalQuote.error}
                  batch={batchMode}
                  privateTransfer={!batchMode && recipientKind === "private"}
                />
              )
            }
            disabled={
              actions.busy ||
              !wallet.connected ||
              !auth.proxyAuthReady ||
              (review === "shield"
                ? !!paymentRequest?.error ||
                  !shieldValue ||
                  depositQuote.loading ||
                  !depositQuote.quote ||
                  shieldValue > depositQuote.quote.max
                : !!sendPreview.error)
            }
            onConfirm={confirmReview}
          />
        )}
        {actions.busy && (
          <BaseOperationProgress
            message={
              status?.type === "info"
                ? status.message
                : "Processing transaction…"
            }
          />
        )}
        {!wallet.connected && linkRequest && (
          <button
            type="button"
            className="wallet-primary"
            disabled={connecting}
            onClick={() => setWalletModalOpen(true)}
          >
            {connecting ? "Connecting…" : "Connect wallet"}
          </button>
        )}
        <fieldset
          className="wallet-form"
          hidden={!!review}
          disabled={actions.busy || !auth.proxyAuthReady}
        >
          {activeTab === "shield" && (
            <SolanaShieldTab
              paymentRecipient={paymentRequest?.recipient}
              paymentError={paymentRequest?.error}
              tokenLocked={!!paymentRequest?.token}
              tokens={network.tokens}
              selectedToken={shieldToken}
              setSelectedToken={setShieldToken}
              amount={shieldAmount}
              memo={shieldMemo}
              setMemo={setShieldMemo}
              walletBalance={walletBalance}
              depositQuote={depositQuote}
              connected={wallet.connected && auth.proxyAuthReady}
              busy={actions.busy}
              onShield={() => setReview("shield")}
            />
          )}
          {activeTab === "swap" && (
            <SolanaSwapTab
              key={`${wallet.address}:${network.programId}`}
              tokens={network.tokens}
              balances={notes.balances}
              balancesLoading={notes.syncing}
              connected={wallet.connected && auth.proxyAuthReady}
              actions={actions}
              onAddFunds={() => setActiveTab("shield")}
            />
          )}
          {activeTab === "receive" && (
            <BaseReceiveTab
              canReceivePrivate={wallet.connected && !!privateAddress}
              privateAddress={privateAddress ?? ""}
              claimedHandle={handles.claimedHandle}
              copyHandle={() => void handles.copyHandle()}
              copyPrivateAddress={copyPrivateAddress}
              copyPaymentLink={copyPaymentLink}
              tokenOptions={receiveTokens}
              tokenNotice={<SolanaTestTokenNotice token={paymentLinkToken} />}
              paymentLinkToken={
                receiveTokens.find(
                  (token) =>
                    token.address ===
                    (paymentLinkToken?.mint ?? (paymentLinkToken ? "sol" : "")),
                ) ?? null
              }
              setPaymentLinkToken={(token) =>
                setPaymentLinkToken(
                  network.tokens.find(
                    (candidate) => (candidate.mint ?? "sol") === token?.address,
                  ) ?? null,
                )
              }
              paymentLinkAmountInput={paymentLinkAmount}
              setPaymentLinkAmountInput={setPaymentLinkAmount}
              paymentLinkDescription="Share a link so someone can pay from their public wallet into your private balance on Solana Devnet."
            />
          )}
          {activeTab === "send" && (
            <SolanaSendTab
              handleStatus={
                handleRecipient.loading
                  ? "Checking handle ownership and Solana receiving keys�"
                  : (handleRecipient.error ??
                    (handleRecipient.address
                      ? "Verified handle � Private Solana transfer"
                      : null))
              }
              network={network}
              feeQuote={withdrawalQuote}
              tokens={network.tokens}
              selectedToken={sendToken}
              setSelectedToken={setSendToken}
              recipient={recipient}
              setRecipient={setRecipient}
              recipientKind={recipientKind}
              amount={sendAmount}
              setAmount={setSendAmount}
              memo={sendMemo}
              setMemo={setSendMemo}
              spendable={displayedSendBalance}
              batchMode={batchMode}
              setBatchMode={setBatchMode}
              batchRows={batchRows}
              setBatchRows={setBatchRows}
              retiredTrees={retiredTrees}
              connected={wallet.connected && auth.proxyAuthReady}
              busy={actions.busy}
              onSend={() => setReview("send")}
              onBatchSend={() => setReview("send")}
              onMigrate={(treeIndex, token) =>
                actions.handleMigrate(treeIndex, token)
              }
            />
          )}
        </fieldset>
      </BaseWalletShell>
    </>
  );
}
